import {
  Component,
  EventEmitter, HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ColDef,
  Column,
  ColumnState,
  StatusPanelDef,
  FirstDataRenderedEvent,
  GetDataPath,
  GetRowIdFunc,
  GridOptions,
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
  SideBarDef,
  CellEditingStoppedEvent,
  CellClickedEvent,
  SelectionChangedEvent,
  RowSelectedEvent,
  FilterModel,
  IsRowSelectable,
  RowClassRules
} from '@ag-grid-community/core';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { ACTION_COLUMN } from '../../../../../config/grid.config';
import {
  ExcelExportParams
} from "@ag-grid-community/core/dist/esm/es6/interfaces/iExcelCreator";
import { StoreService } from '../../../../../services/store.service';
import { GridState, SettingsKey } from '../../../../../model/settings.model';

@Component({
  selector: 'grid-master',
  templateUrl: './grid-master.component.html',
})
export class GridMasterComponent implements OnInit, OnDestroy {

  @Input() height: string;
  @Input() columnDefs: ColDef[] = [];
  @Input() rowData: any[] | undefined | null;
  @Input() headersKey = '';
  @Input() exportKey: string | null = null;
  @Input() sizeColumnsToFit = false;
  @Input() rowHover = false;
  @Input() suppressScrollOnNewData = true;
  @Input() treeData = false;
  @Input() selectedIds: number[] = [];
  @Input() lockedIds: number[] = [];
  @Input() context = {};
  @Input() getDataPath: GetDataPath;
  @Input() getLocaleText = this.localeText.bind(this);
  @Input() getRowId: GetRowIdFunc;
  @Input() gridOptions:GridOptions = {};
  @Input() autoGroupColumnDef: ColDef | undefined = undefined;
  @Input() groupDefaultExpanded: number;
  @Input() restoreFilters = false;
  @Input() rowClassRules?: RowClassRules;
  @Input() rowSelection: 'single' | 'multiple' = 'multiple'
  @Input() suppressRowClickSelection = true;
  @Input() isRowSelectable: IsRowSelectable = () => true;
  @Output() rowClickedEvent = new EventEmitter<any>()
  @Output() cellClickedEvent = new EventEmitter<CellClickedEvent>()
  @Output() cellEditedEvent = new EventEmitter<any>();
  @Output() selectionChangedEvent = new EventEmitter<any>();

  gridApi: GridApi;
  private _subscription = new Subscription();
  private _saveStateTimeoutId: ReturnType<typeof setTimeout> | null = null;

  getContextMenuItems = () => [];
  selectedRows: any[] = [];

  @Input() sideBar: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivots: true,
          suppressPivotMode: true,
          suppressValues: true,
          suppressRowGroups: true
        }
      }
    ]
  };

  @Input() statusBar: {
    statusPanels: StatusPanelDef[];
  } = {
    statusPanels: [
      { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
      { statusPanel: 'agTotalRowCountComponent', align: 'center' },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
      { statusPanel: 'agAggregationComponent' },
    ],
  };

  @Input() defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
    headerValueGetter: this.localizeHeader.bind(this),
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
  };

  prefix = '-grid-state';
  filtersConfigName: string;

  constructor(
    private _translate: TranslateService,
    private _datePipe: DatePipe,
    private _store: StoreService,
  ) {
  }

  ngOnInit(): void {
    this._subscription.add(
      this._store.gridRestoreSetup.subscribe((result) => {
          if (result) {
            this.restoreSetup(result);
          }
        }
      ),
    );

    this.filtersConfigName = this.headersKey + '-grid-filters-model';
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    if (this._saveStateTimeoutId !== null) {
      clearTimeout(this._saveStateTimeoutId);
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this._fitColumns();
  }

  reset(): void {
    this.gridApi.resetColumnState();
    this.gridApi.resetColumnGroupState();
    this.gridApi.setFilterModel(null);
    this.gridApi.deselectAll();
    this.gridApi.applyColumnState({ state: undefined });
    this._fitColumns();
    this.saveState();
  }

  private _fitColumns(): void {
    if (this.sizeColumnsToFit && window.innerWidth > 1200) {
      this.gridApi.sizeColumnsToFit();
    } else {
      this.gridApi.autoSizeAllColumns();
    }
  }

  saveState(): void {
    if (!this.headersKey || !this.gridApi) {
      return;
    }

    if (this._saveStateTimeoutId !== null) {
      clearTimeout(this._saveStateTimeoutId);
    }

    // AG Grid can emit column events before the final visibility state settles,
    // especially when columns are toggled from the side panel.
    this._saveStateTimeoutId = setTimeout(() => {
      localStorage.setItem(this.headersKey + this.prefix, JSON.stringify(this.gridApi.getColumnState()));
      this._saveStateTimeoutId = null;
    });
  }

  saveFilters(): void {
    if (this.restoreFilters ) {
      localStorage.setItem(this.filtersConfigName, JSON.stringify(this.gridApi.getFilterModel()))
    }
  }


  restoreState(): void {
    if (this.headersKey) {
      const state = localStorage.getItem(this.headersKey + this.prefix);
      if (state) {
        this.gridApi.applyColumnState( { applyOrder: true, state: this.withMissingColumns(JSON.parse(state)) });
      }
    }

    //special handler for special requirement
    if (this.headersKey === SettingsKey.TASKS_GRID) {
      this.gridApi.applyColumnState({ state: [{ colId: 'assigned_to_team', hide: true }] });
      this.gridApi.applyColumnState({ state: [{ colId: 'source', hide: true }] });
    }
  }

  // Kolumny dodane po zapisaniu stanu trafiłyby przy applyOrder na koniec gridu.
  // Wstawiamy je za kolumną, która poprzedza je w domyślnej definicji, z domyślną widocznością.
  private withMissingColumns(savedState: ColumnState[]): ColumnState[] {
    const state = [...savedState];
    const savedIds = new Set(state.map(column => column.colId));
    const defaultIds = this.gridApi.getColumns()?.map(column => column.getColId()) ?? [];

    defaultIds.forEach((colId, index) => {
      if (savedIds.has(colId)) {
        return;
      }

      const previousIndex = index > 0 ? state.findIndex(column => column.colId === defaultIds[index - 1]) : -1;
      const hide = !!this.gridApi.getColumn(colId)?.getColDef().hide;
      state.splice(previousIndex + 1, 0, { colId, hide });
      savedIds.add(colId);
    });

    return state;
  }

  private restoreFiltersFromStorage(): void {
    const filterState = localStorage.getItem(this.filtersConfigName);
    if (filterState) {
      this.gridApi.setFilterModel(JSON.parse(filterState))
    }
  }

  exportXLS() {
    const prefix = 'GRID_EXPORT.'
    const fileName = this._translate.instant(prefix + (this.exportKey || this.headersKey))
    const datePrefix = this._datePipe.transform(new Date(), 'YYYYMMdd');
    let params: ExcelExportParams = {
      fileName: `${datePrefix}-${fileName}`,
      columnKeys: this._filterColumnsForExport(),
      exportedRows: 'filteredAndSorted'
    }

    this.gridApi.exportDataAsExcel(params);
  }

  private _filterColumnsForExport(): Column<any>[] | undefined {
    return this.gridApi.getColumns()?.filter(column => {
      return column.getColDef().headerName !== ACTION_COLUMN && column.isVisible()
    })
  }

  localizeHeader(parameters: any): string {
    const colDef = parameters.colDef;
    const headerIdentifier = colDef?.headerName as string;
    let key = this.headersKey ? `${this.headersKey}.${headerIdentifier}` : colDef?.headerName;
    return this._translate.instant(key)
  }

  localeText(params: any): string {
    const gridKey = 'GRID.' + params.key;
    return this._translate.instant(gridKey);
  }

  onFirstDataRendered(params: FirstDataRenderedEvent) {
    this._fitColumns();
    this.setSelectedRows(this.selectedIds);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.restoreState();
  }

  rowClicked(event: RowClickedEvent<any>) {
    this.rowClickedEvent.emit(event);
  }

  cellClicked(event: CellClickedEvent<any>) {
    this.cellClickedEvent.emit(event);
  }

  onRowSelected(event: RowSelectedEvent) {
    const node = event.node;
    if (node.data && this.lockedIds.includes(node.data.id) && !node.isSelected()) {
      node.setSelected(true);
      return;
    }

    this.selectedRows = this.gridApi.getSelectedRows();
  }

  setSelectedRows(ids: number[]): void {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.forEachNode(node => {
      if (ids.includes(node.data.id)) {
        node.setSelected(true);
      }
    })
  }

  cellEditingStopped(event: CellEditingStoppedEvent) {
    this.cellEditedEvent.emit(event)
  }

  selectionChanged(event: SelectionChangedEvent) {
    this.selectionChangedEvent.emit(event);
  }

  saveSetup(key: SettingsKey, name: string): void {
    const filterModel: FilterModel = this.gridApi.getFilterModel();
    const columnState = this.gridApi.getColumnState();
    const config: GridState = { name, filterModel, columnState };
    this._store.saveGridSettings(key, config);
  }

  restoreSetup(setup: GridState): void {
    const { columnState, filterModel }: GridState = setup;
    this.gridApi.applyColumnState( { applyOrder: true, state: this.withMissingColumns(columnState) });
    this.gridApi.setFilterModel(filterModel);

    if (setup.showMsg) {
      this._store.showSuccessMsg('SETTINGS.RESTORE_SUCCESS')
    }
  }

  clearSelectedRows(): void {
    if (!this.gridApi) {
      this.selectedRows = [];
      return;
    }

    this.gridApi.deselectAll();
    // Zablokowane wiersze (lockedIds) zostają zaznaczone przez onRowSelected
    this.selectedRows = this.gridApi.getSelectedRows();
  }
}



